import {
  DisplayData,
  isPlural,
  LabelPlurality,
  LabelPluralityMap,
  LabelVerbosity,
  Precision,
  UnitMap,
  BasicConversionArgs,
  makePrecise
} from '.'
import { IDistance, DistanceUnit } from '@eventops/athlinks-results-model'
import { capitalize as loCap } from 'lodash'
import * as convert from 'conversions'
import {UnitLog} from '..'

function buildShortLabelMap(unit: DistanceUnit): LabelPluralityMap {
  return {
    singular: unit,
    plural: unit
  }
}

function buildDistanceLabelMap(): UnitMap {
  let map = {}
  map[DistanceUnit.FEET] = () => ({
    'short': buildShortLabelMap(DistanceUnit.FEET),
    'standard': {
      singular: 'foot',
      plural: 'feet'
    }
  })
  map[DistanceUnit.METERS] = () => ({
    'short': buildShortLabelMap(DistanceUnit.METERS),
    'standard': {
      singular: 'meter',
      plural: 'meters'
    }
  })
  map[DistanceUnit.HECTA_METERS] = () => ({
    'short': buildShortLabelMap(DistanceUnit.HECTA_METERS),
    'standard': {
      singular: '100 meter',
      plural: '100 meters'
    }
  })
  map[DistanceUnit.HECTA_YARDS] = () => ({
    'short': buildShortLabelMap(DistanceUnit.HECTA_YARDS),
    'standard': {
      singular: '100 yard',
      plural: '100 yards'
    }
  })
  map[DistanceUnit.YARDS] = () => ({
    'short': buildShortLabelMap(DistanceUnit.YARDS),
    'standard': {
      singular: 'yard',
      plural: 'yards'
    }
  })
  map[DistanceUnit.MILES] = () => ({
    'short': buildShortLabelMap(DistanceUnit.MILES),
    'standard': {
      singular: 'mile',
      plural: 'miles'
    }
  })
  map[DistanceUnit.KILO_METERS] = () => ({
    'short': buildShortLabelMap(DistanceUnit.KILO_METERS),
    'standard': {
      singular: 'kilometer',
      plural: 'kilometers'
    }
  })
  // The default case
  map['undefined'] = () => ({
    'short': buildShortLabelMap(DistanceUnit.METERS),
    'standard': {
      singular: 'meter',
      plural: 'meters'
    }
  })
  return map
}

const distanceLabelLongMap = buildDistanceLabelMap()

export function findLabel(
  magnitude: number,
  unit: DistanceUnit,
  verbosity: LabelVerbosity
): string {
  // There is no standard 'long' format for distance labels
  const adjustedVerbosity =
    (verbosity === LabelVerbosity.LONG) ? LabelVerbosity.STANDARD : verbosity
  const plurality =
    (isPlural(magnitude)) ? LabelPlurality.PLURAL : LabelPlurality.SINGULAR
  return distanceLabelLongMap[unit]()[adjustedVerbosity][plurality]
}

export interface DistanceConversionArgs extends BasicConversionArgs {
  precision?: Precision
}

export function convertDistanceValue(
  distance: IDistance,
  args: DistanceConversionArgs = {}
): number {
  const {
    distanceInMeters,
    distanceUnit
  } = distance
  const {
    precision = 1
  } = args

  // hecta meters are a special case
  if (distanceUnit === DistanceUnit.HECTA_METERS) {
    return makePrecise(
      convert(
        distanceInMeters,
        'meters',
        DistanceUnit.METERS
      ) / 100, precision, distanceUnit)
  }
  // hecta yards are a special case
  if (distanceUnit === DistanceUnit.HECTA_YARDS) {
    return makePrecise(
      convert(
        distanceInMeters,
        'meters',
        DistanceUnit.YARDS
      ) / 100, precision, distanceUnit)
  }
  return makePrecise(
    convert(
      distanceInMeters,
      'meters',
      distanceUnit
    ), precision, distanceUnit)
}

export function convertDistance(
  distance: IDistance,
  args: DistanceConversionArgs = {
    precision: 1
  }
): DisplayData<number> {
  const {
    distanceInMeters,
    distanceUnit
  } = distance
  if (distanceInMeters < 0) {
    UnitLog.warn(`Negative distance encountered ` +
    `(${distanceInMeters}), normalizing to 0.`)
    return {
      value: 0,
      label: ''
    }
  }
  const {
    verbosity = LabelVerbosity.SHORT,
    capitalize = false
  } = args
  try {
    const value = convertDistanceValue(distance, args)
    const label = findLabel(value, distanceUnit, verbosity)
    return {
      value,
      label: (capitalize) ? loCap(label) : label
    }
  } catch {
    UnitLog.warn(`Distance unit '${distanceUnit}' is not supported.`)
    return {
      value: distance.distanceInMeters,
      label: DistanceUnit.METERS
    }
  }
}